/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CONFIRM_MODAL_SETTING, C_HEALTH_RECORD_DEFAULT } from './constants'
import { EExamType } from './enums'
import { EFormFieldExam } from './enums/EFormField'
import { IHealthRecord, IPartExam } from './interfaces'
import { Form, FormInstance, notification } from 'antd'
import { DEBOUNCE_TIME } from 'constants/common'
import { EStatus } from 'enums'
import { EUnit } from 'enums/EUnit'
import { useGetBookingDetail, useModalConfirm } from 'hooks'
import { t } from 'i18next'
import { IResponse } from 'interfaces/services'
import moment from 'moment'
import { EMajorType, ETemplateExamType } from 'pages/accountSystemSetting/enums'
import { EObstetricsAndGynecology } from 'pages/accountSystemSetting/enums/EObstetricsAndGynecology'
import { ITemplateExamItem } from 'pages/accountSystemSetting/interfaces'
import { EBookingType, EExamStatus } from 'pages/patientBooking/enums'
import { IBooking } from 'pages/patientBooking/interfaces'
import { EPaymentMethod } from 'pages/patientPayment/enums'
import {
  IBookingDetail,
  ICreateBooking,
  IGetPriceQuery,
  IPatient
} from 'pages/patientReceive/interfaces'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useBookingServices,
  useExamService,
  useReceiveServices,
  useSystemSettingService
} from 'services'
import {
  addDays,
  calculateDaysDifference,
  debounce,
  formatDate,
  getDateAddMinute,
  getKeyNote
} from 'utils'

export interface ExamContextProps {
  bookingDetail: IBookingDetail
  handleCreateUpdateHealthRecord: (query: IHealthRecord) => void
  form: FormInstance<any>
  loading: boolean
  handleOnChangeForm: (examType?: EExamType, data?: any) => void
  submitExamFinished: (action?: (data?: any) => void) => void
  addSubService: (ids: Array<number>) => void
  removeSubService: (ids: Array<number>) => void
  reloadBookingDetail: () => Promise<IBookingDetail>
  updateMedicalHistory: (id: number, data?: any) => Promise<IResponse<any>>
  listPatientRef: any
  templates: Array<ITemplateExamItem>
  setTemplates: (tp: Array<ITemplateExamItem>) => void
  loadingSubmit: boolean
  setLoadingSubmit: (loading: boolean) => void
  setRadioObstetricsGynecology: (r: EObstetricsAndGynecology) => void
  setBookings: (b: Array<IBooking>) => void
  bookings: Array<IBooking>
  radioObstetricsGynecology: EObstetricsAndGynecology
  setReExamDate: (date: any) => void
  reExamDate: any
  countReExam: any
  setCountReExam: (count: number) => void
  updatePrescription: (medicines: any, query: any) => Promise<any>
  setIsAutoFill: (isAutoFill: boolean) => void
  isAutoFill: boolean
  isDisableAutoFill: boolean
  checkValidForm: (data: boolean) => void
  valid: boolean
  patient: IPatient
}

export const ExamContext = React.createContext<ExamContextProps | null>(null)

export const useExamContext = () => {
  const context = React.useContext(ExamContext)
  if (context === null) {
    throw new Error('Missing CashTransferProvider')
  }
  return context
}

export const ExamProvider = (props: any) => {
  const {
    bookingDetail,
    reloadBookingDetail,
    loading,
    setBookingDetail,
    updateHeathRecordByAnotherBooking
  } = useGetBookingDetail()
  const { children, value } = props
  const { updateExamStatus, createBooking, getPriceBooking } =
    useReceiveServices()
  const {
    createUpdateHealthRecord,
    createPrescriptions,
    inquiryPrescriptions
  } = useExamService()
  const { getPatientById } = useBookingServices()
  const healthRecordRef = useRef<IHealthRecord>(C_HEALTH_RECORD_DEFAULT)
  const healthRecordBeforeAutoFillRef = useRef<IHealthRecord>(
    healthRecordRef.current
  )
  const { onConfirmAccept } = useModalConfirm('confirm-cancel', true)
  const [templates, setTemplates] = useState<Array<ITemplateExamItem>>([])
  const { searchTemplateDefaultActiveWithMajor } = useSystemSettingService()
  const [form] = Form.useForm()
  const listPatientRef = useRef<any>(null)
  const isHasDiagnosisSolution = useRef(false)
  const isUpdated = useRef(true)
  const bookingsRef = useRef<Array<IBooking>>()
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [reExamDate, setReExamDate] = useState<any>()
  const [countReExam, setCountReExam] = useState<any>()
  const [radioObstetricsGynecology, setRadioObstetricsGynecology] =
    useState<string>(EObstetricsAndGynecology.Obstetrics)
  const [isAutoFill, setIsAutoFill] = useState(true)
  const { getPatientBookings } = useExamService()
  const [patient, setPatient] = useState<IPatient>()
  const isObstetricsAndGynecology =
    bookingDetail?.doctor?.major?.id === EMajorType.ObstetricsAndGynecology

  const setBookings = (bookings: Array<IBooking>) => {
    bookingsRef.current = bookings
  }

  const temporaryPrescription =
    bookingDetail?.health_record?.clinic_metadata?.temporary_prescription

  const handleSetAutoFill = (isAutoFill: boolean) => {
    setIsAutoFill(isAutoFill)
    if (isAutoFill && bookingDetail.patient_id) {
      healthRecordBeforeAutoFillRef.current = JSON.parse(
        JSON.stringify(healthRecordRef.current)
      )
      getPatientBookings(bookingDetail.patient_id).then((res) => {
        if (res._successfully) {
          if (!res.data.length) {
            setIsAutoFill(false)
            notification.error({ message: t('patient-has-not-history-exam') })
            return
          }
          const dataSort = res.data
            ?.filter(
              (item) => item?.major_id === bookingDetail?.doctor?.major.id
            )
            .sort((a, b) => b?.finished_at - a?.finished_at)
          if (!dataSort.length) {
            setIsAutoFill(false)
            notification.error({ message: t('patient-has-not-history-exam') })
          }

          updateHeathRecordByAnotherBooking(dataSort[0].booking_id).then(
            (res) => {
              healthRecordRef.current = {
                ...healthRecordRef.current,
                examinations: {
                  ...healthRecordRef.current.examinations,
                  body_part_examination:
                    res?.examinations?.body_part_examination,
                  physical_examination: res?.examinations?.physical_examination,
                  subclinical_diagnosis:
                    res?.examinations.subclinical_diagnosis,
                  template_index: [
                    {
                      name: res?.examinations?.template_index?.[0]
                        .name as string
                    }
                  ]
                },
                diagnosis: res?.diagnosis
              }
              setRadioObstetricsGynecology(
                res?.examinations?.template_index?.[0].name as string
              )
              createUpdateHealthRecord({
                ...healthRecordRef.current,
                health_record_id: bookingDetail.health_record_id
              }).finally(() => reloadBookingDetail())
            }
          )
        }
      })
    } else {
      if (bookingDetail.booking_id) {
        healthRecordBeforeAutoFillRef.current['diagnosis'] = {
          [EFormFieldExam.Solution]:
            healthRecordBeforeAutoFillRef.current?.diagnosis?.solution || '',
          [EFormFieldExam.Diagnosis]:
            healthRecordBeforeAutoFillRef.current?.diagnosis?.diagnosis || ''
        }
        createUpdateHealthRecord(healthRecordBeforeAutoFillRef.current).then(
          (res) => {
            if (res._successfully) {
              healthRecordRef.current = healthRecordBeforeAutoFillRef.current
              reloadBookingDetail()
            }
          }
        )
      }
    }
  }

  const getTemplateValueDefault = (templates: Array<ITemplateExamItem>) => {
    return templates?.map((item, index) => {
      return {
        key: item?.name,
        value: item?.options?.[0] || item?.value,
        name: item?.name
      } as IPartExam
    })
  }

  const dataTemplates = useMemo(() => {
    const templatesPreExamination = templates?.filter(
      (template) => template.type === ETemplateExamType.PreExamination
    )
    const templatesGeneral = templates?.filter(
      (template) => template.type === ETemplateExamType.General
    )
    const templatesPart = templates?.filter(
      (template) => template.type === ETemplateExamType.Part
    )

    const templateObstetricsGynecology =
      radioObstetricsGynecology === EObstetricsAndGynecology.Obstetrics
        ? templatesPart?.filter(
            (template) =>
              template.tags?.includes(EObstetricsAndGynecology.Obstetrics) ||
              !template?.tags?.length
          )
        : templatesPart?.filter((template) =>
            template.tags?.includes(EObstetricsAndGynecology.Gynecology)
          )
    const bodyPartExamination = getTemplateValueDefault(
      isObstetricsAndGynecology ? templateObstetricsGynecology : templatesPart
    )
    const pathologicalProcess = getTemplateValueDefault(templatesPreExamination)
    const physicalExamination = getTemplateValueDefault(templatesGeneral)
    return {
      bodyPartExamination,
      pathologicalProcess,
      physicalExamination
    }
  }, [templates, isObstetricsAndGynecology, radioObstetricsGynecology])

  useEffect(() => {
    if (bookingDetail?.patient_id) {
      getPatientById(bookingDetail?.patient_id).then((res) => {
        setPatient(res.data)
      })
    }
  }, [bookingDetail?.patient_id])

  useEffect(() => {
    const reExaminationDate = temporaryPrescription?.query?.re_examination_date
      ? new Date(temporaryPrescription?.query?.re_examination_date)
      : undefined
    handleSetReExamDate({
      time: reExaminationDate,
      date: reExaminationDate
    })
  }, [temporaryPrescription?.query?.re_examination_date])

  useEffect(() => {
    if (
      healthRecordRef.current?.health_record_id &&
      new Date(reExamDate?.date)?.getTime() !==
        temporaryPrescription?.query?.re_examination_date
    ) {
      updateTemporaryReExaminationDate(
        reExamDate?.date ? new Date(reExamDate?.date)?.getTime() : undefined
      )
    }
  }, [reExamDate?.date, healthRecordRef.current?.health_record_id])

  useEffect(() => {
    setIsAutoFill(false)
  }, [bookingDetail.booking_id])

  useEffect(() => {
    if (templates.length && !isUpdated.current) {
      healthRecordRef.current.examinations.body_part_examination =
        dataTemplates.bodyPartExamination
      // healthRecordRef.current.examinations.pathological_process =
      //   dataTemplates.pathologicalProcess
      healthRecordRef.current.examinations.physical_examination =
        dataTemplates.physicalExamination
      isUpdated.current = true
      handleCreateUpdateHealthRecord({
        ...bookingDetail.health_record?.clinic_metadata,
        health_record_id: bookingDetail.health_record_id
      })
    }
  }, [templates])

  useEffect(() => {
    if (bookingDetail?.doctor?.major.id) {
      searchTemplateDefaultActiveWithMajor(
        bookingDetail?.doctor?.major.id
      ).then((res) => {
        if (res?._successfully) {
          const examination_template =
            res?.data?.examination_templates[
              res?.data?.examination_templates?.length - 1
            ]
          if (examination_template.status === EStatus.Active) {
            const template = examination_template.metadata?.templates?.filter(
              (template) => !template?.is_hidden
            )
            setTemplates(template || [])
          }
        }
      })
    }
  }, [bookingDetail?.doctor?.major.id])

  useEffect(() => {
    if (bookingDetail.exam_status === EExamStatus.Waiting) {
      isUpdated.current = false
    }
  }, [bookingDetail?.booking_id])

  const getItemFromKey = (list?: Array<IPartExam>, key?: string) => {
    return list?.find((item) => item?.key === key) || undefined
  }

  useEffect(() => {
    if (dataTemplates && patient && bookingDetail) {
      let dataFormBodyPart = {}
      let dataFormPhysical = {}
      let dataFormGeneral = {}
      const subclinicalDiagnosis = getItemFromKey(
        bookingDetail?.health_record?.clinic_metadata?.examinations
          ?.subclinical_diagnosis,
        EFormFieldExam.SubclinicalDiagnosis
      )
      const solution =
        bookingDetail?.health_record?.clinic_metadata?.diagnosis?.solution
      const diagnosis =
        bookingDetail?.health_record?.clinic_metadata?.diagnosis?.diagnosis
      const bodyPartExamination =
        bookingDetail?.health_record?.clinic_metadata?.examinations
          ?.body_part_examination
      const physicalExamination =
        bookingDetail?.health_record?.clinic_metadata?.examinations
          ?.physical_examination
      const pathologicalProcess =
        bookingDetail?.health_record?.clinic_metadata?.examinations
          ?.pathological_process ||
        patient?.latest_health_record?.examinations?.pathological_process
      dataTemplates.bodyPartExamination?.forEach((item, index) => {
        const findBodyPart = bodyPartExamination?.find(
          (i) => i.name === item.name
        )
        dataFormBodyPart = {
          ...dataFormBodyPart,
          [item.name || '']: findBodyPart?.value || item.value,
          [getKeyNote(item.name!)]: findBodyPart?.note
        }
      })

      dataTemplates.physicalExamination?.forEach((item, index) => {
        const findPhysicalExamination = physicalExamination?.find(
          (i) => i.name === item.name
        )
        dataFormPhysical = {
          ...dataFormPhysical,
          [item.name || '']: findPhysicalExamination?.value || item.value,
          [getKeyNote(item.name!)]: findPhysicalExamination?.note
        }
      })

      dataTemplates.pathologicalProcess?.forEach((item, index) => {
        const findPathologicalProcess = pathologicalProcess?.find(
          (i) => i.name === item.name
        )
        dataFormGeneral = {
          ...dataFormGeneral,
          [item.name || '']: findPathologicalProcess?.value || item.value,
          [getKeyNote(item.name!)]: findPathologicalProcess?.note
        }
      })
      const dataForm: any = {
        [EFormFieldExam.Solution]: solution,
        [EFormFieldExam.Diagnosis]: diagnosis,
        [EFormFieldExam?.SubclinicalDiagnosis]: subclinicalDiagnosis?.value,
        ...dataFormBodyPart,
        ...dataFormPhysical,
        ...dataFormGeneral
      }

      form.setFieldsValue(dataForm)

      healthRecordRef.current = {
        health_record_id: bookingDetail?.health_record_id,
        examinations: {
          body_part_examination: dataTemplates.bodyPartExamination?.map(
            (item) => ({
              ...item,
              value: dataForm?.[item?.name as any],
              note: dataForm[getKeyNote(item.name!)]
            })
          ),
          pathological_process: dataTemplates.pathologicalProcess?.map(
            (item) => ({
              ...item,
              value: dataForm?.[item?.name as any],
              note: dataForm[getKeyNote(item.name!)]
            })
          ),
          physical_examination: dataTemplates.physicalExamination?.map(
            (item) => ({
              ...item,
              value: dataForm?.[item?.name as any],
              note: dataForm[getKeyNote(item.name!)]
            })
          ),
          subclinical_diagnosis: [
            {
              key: EFormFieldExam.SubclinicalDiagnosis,
              value: subclinicalDiagnosis?.value,
              name: EFormFieldExam.SubclinicalDiagnosis
            }
          ]
        },
        diagnosis: {
          diagnosis,
          solution
        }
      }
      return
    }
  }, [bookingDetail, patient])

  useEffect(() => {
    if (isAutoFill === true) return
    setRadioObstetricsGynecology(
      bookingDetail?.health_record?.clinic_metadata?.examinations
        .template_index?.[0].name || EObstetricsAndGynecology.Obstetrics
    )
  }, [bookingDetail])

  const handleCreateUpdateHealthRecord = useCallback(
    debounce((query: IHealthRecord) => {
      if (bookingDetail.exam_status === EExamStatus.Finished) return
      createUpdateHealthRecord(query).then((res) => {})
    }, DEBOUNCE_TIME),
    []
  )

  const handleBuildData = (items?: Array<IPartExam>, data?: any) => {
    return items?.map((item, index) => {
      if (data?.[item.name!]) {
        return {
          key: item.name,
          value: data[item.name!],
          name: item.name,
          note: data?.[`${item.name}Note`]
        } as IPartExam
      } else {
        return item as IPartExam
      }
    })
  }
  const handleBuildData2 = (items?: Array<IPartExam>, data?: any) => {
    return items?.map((item, index) => {
      if (data?.[item.name!]) {
        return {
          key: item.name,
          value: data[item.name!],
          name: item.name,
          note: data?.[`${item.name}Note`]
        } as IPartExam
      } else {
        return item as IPartExam
      }
    })
  }
  const handleOnChangeForm = (examType?: EExamType) => {
    const formData = form?.getFieldsValue()
    isHasDiagnosisSolution.current =
      !!formData?.diagnosis && !!formData?.solution
    switch (examType) {
      case EExamType.Diagnosis:
        healthRecordRef.current.diagnosis = {
          [EFormFieldExam.Solution]: formData?.[EFormFieldExam.Solution],
          [EFormFieldExam.Diagnosis]: formData?.[EFormFieldExam.Diagnosis]
        }
        const physicalExamination = handleBuildData2(
          healthRecordRef.current.examinations.physical_examination,
          formData
        )
        const bodyPartExamination = handleBuildData(
          healthRecordRef.current.examinations.body_part_examination,
          formData
        )
        healthRecordRef.current.examinations = {
          ...healthRecordRef.current.examinations,
          body_part_examination: bodyPartExamination,
          physical_examination: physicalExamination
        }
        break
      case EExamType.BodyPartExamination: {
        const bodyPartExaminationCache =
          healthRecordRef.current.examinations.body_part_examination

        const bodyPartExamination = handleBuildData(
          bodyPartExaminationCache,
          formData
        )

        healthRecordRef.current.examinations = {
          ...healthRecordRef.current.examinations,
          body_part_examination: bodyPartExamination
        }
        break
      }
      case EExamType.BodyPartExaminationGynecology:
      case EExamType.BodyPartExaminationObstetrics: {
        const tag =
          examType === EExamType.BodyPartExaminationGynecology
            ? EObstetricsAndGynecology.Obstetrics
            : EObstetricsAndGynecology.Gynecology
        const bodyPartExaminationCache =
          healthRecordRef.current.examinations.body_part_examination
        const bodyPartExamination = handleBuildData(
          bodyPartExaminationCache,
          formData
        )?.map((item) => ({ ...item, tag: tag }))
        healthRecordRef.current = {
          ...healthRecordRef.current,
          examinations: {
            ...healthRecordRef.current.examinations,
            body_part_examination: bodyPartExamination
          }
        }
        break
      }
      case EExamType.PathologicalProcess: {
        const pathologicalProcessCache =
          healthRecordRef.current.examinations.pathological_process

        const pathologicalProcess = handleBuildData(
          pathologicalProcessCache,
          formData
        )
        healthRecordRef.current = {
          ...healthRecordRef.current,
          examinations: {
            ...healthRecordRef.current.examinations,
            pathological_process: pathologicalProcess
          }
        }
        break
      }
      case EExamType.PhysicalExamination: {
        const physicalExaminationCache =
          healthRecordRef.current.examinations.physical_examination
        const physicalExamination = handleBuildData(
          physicalExaminationCache,
          formData
        )

        healthRecordRef.current = {
          ...healthRecordRef.current,
          examinations: {
            ...healthRecordRef.current.examinations,
            physical_examination: physicalExamination
          }
        }
        break
      }
      case EExamType.SubclinicalDiagnosis: {
        const subclinicalDiagnosis = [
          {
            key: EFormFieldExam.SubclinicalDiagnosis,
            value: formData?.[EFormFieldExam.SubclinicalDiagnosis],
            name: EFormFieldExam.SubclinicalDiagnosis
          }
        ]
        healthRecordRef.current = {
          ...healthRecordRef.current,
          examinations: {
            ...healthRecordRef.current.examinations,
            subclinical_diagnosis: subclinicalDiagnosis
          }
        }
        break
      }
    }
    healthRecordRef.current.examinations.template_index = [
      { name: radioObstetricsGynecology }
    ]
    handleCreateUpdateHealthRecord(healthRecordRef.current)
  }

  const submitExamFinished = async (action?: (data?: any) => void) => {
    if (form.getFieldValue('solution') && form.getFieldValue('diagnosis'))
      isHasDiagnosisSolution.current = true
    else isHasDiagnosisSolution.current = false

    if (!isHasDiagnosisSolution.current) {
      notification.error({ message: t('please-enter-diagnosis-solution') })
      return
    }

    setLoadingSubmit(true)

    await reloadBookingDetail()

    const updateExamFinished = async () => {
      const queryCreatePrescriptions =
        bookingDetail?.health_record?.clinic_metadata?.temporary_prescription
          ?.query
      if (queryCreatePrescriptions?.medicines?.length) {
        const res = await createPrescriptions(queryCreatePrescriptions)
        if (!res?._successfully) {
          setLoadingSubmit(false)
          return res
        } else {
          if (res?.data) {
            setBookingDetail({ ...bookingDetail, prescription: res.data })
          }
        }
      }
      setLoadingSubmit(true)
      return updateExamStatus({
        booking_id: bookingDetail?.booking_id || 0,
        status: EExamStatus.Finished
      }).then((res) => {
        if (res._successfully) {
          listPatientRef.current?.reloadSearchBookings()
          setBookingDetail(
            (bookingDetail) =>
              ({
                ...bookingDetail,
                exam_status: EExamStatus.Finished
              } as any)
          )
          if (action) action(res?.data)
          // create new booking with re exam

          if (reExamDate.time) {
            let subServiceIds: any = []

            bookingDetail?.metadata?.initial_services?.services?.forEach(
              (sv: any) => {
                const sub_Ids = sv?.sub_services?.map((ssv: any) => ssv?.id)
                if (sub_Ids) {
                  subServiceIds = [...subServiceIds, ...sub_Ids]
                }
              }
            )
            const queryGetPrice = {
              clinic_id: bookingDetail.clinic_id,
              patient_id: bookingDetail?.patient_id,
              sub_service_ids: subServiceIds,
              payment_method: EPaymentMethod.DirectPayment
            } as IGetPriceQuery
            getPriceBooking(queryGetPrice).then((res) => {
              if (res?._successfully) {
                const query = {
                  from: new Date(reExamDate.date).getTime(),
                  to: getDateAddMinute(
                    new Date(reExamDate.date),
                    30
                  )?.getTime(),
                  patient_id: bookingDetail?.patient_id,
                  sub_service_ids: [],
                  currency: EUnit.Point,
                  booking_type: EBookingType.Offline,
                  payment_method: EPaymentMethod.DirectPayment,
                  clinic_id: bookingDetail.clinic_id,
                  user_paying_amount: res?.data?.patient_paying_amount,
                  re_examination: true,
                  created_by:
                    bookingDetail?.created_by_status === 'amaz_care'
                      ? 'recreate_amaz_care'
                      : 'recreate_clinic'
                } as ICreateBooking
                createBooking(query)
              }
            })
          }
        } else {
          setLoadingSubmit(false)
        }
        return res
      })
    }

    onConfirmAccept(CONFIRM_MODAL_SETTING, updateExamFinished, () => {
      setLoadingSubmit(false)
      reloadBookingDetail()
    })
  }

  const updateMedicalHistory = async (ids: Array<number>) => {
    return createUpdateHealthRecord({
      health_record_id: healthRecordRef.current.health_record_id,
      examinations: {
        body_part_examination: undefined,
        pathological_process: undefined,
        physical_examination: undefined,
        subclinical_diagnosis: undefined
      },
      outpatient_report_ids: ids
    })
  }

  const handleOnChangRadio = (value: EObstetricsAndGynecology) => {
    const templatesPart = templates?.filter(
      (template) => template.type === ETemplateExamType.Part
    )
    const templateObstetricsGynecology =
      value === EObstetricsAndGynecology.Obstetrics
        ? templatesPart?.filter(
            (template) =>
              template.tags?.includes(EObstetricsAndGynecology.Obstetrics) ||
              !template?.tags?.length
          )
        : templatesPart?.filter((template) =>
            template.tags?.includes(EObstetricsAndGynecology.Gynecology)
          )
    const bodyPartExamination = getTemplateValueDefault(
      templateObstetricsGynecology
    )
    healthRecordRef.current.examinations.body_part_examination =
      bodyPartExamination
    setRadioObstetricsGynecology(value)
    healthRecordRef.current.examinations.template_index = [{ name: value }]
    if (bookingDetail?.exam_status === EExamStatus.Finished) return
    handleCreateUpdateHealthRecord(healthRecordRef.current)
  }

  const handleSetCountReExam = (count: number) => {
    setCountReExam(count)
    if (bookingDetail?.from) {
      setReExamDate({
        date: moment(addDays(new Date(bookingDetail?.from), count)),
        time:
          reExamDate?.time ||
          moment(addDays(new Date(bookingDetail?.from), count))
      })
    }
    if (!count) {
      setReExamDate(null)
    }
  }

  const handleSetReExamDate = (date: any) => {
    const countDate = calculateDaysDifference(
      formatDate(new Date(), 'MM/DD/YYYY'),
      formatDate(new Date(date?.date), 'MM/DD/YYYY')
    )
    setCountReExam(countDate)
    if (date.time) {
      setReExamDate(date)
    } else {
      setReExamDate({ ...reExamDate, date: date.date })
    }
  }

  const updatePrescription = async (medicines: any, query: any) => {
    const queryTemporary =
      bookingDetail?.health_record?.clinic_metadata?.temporary_prescription
        ?.query
    const mergedQuery = { medicines, query: { ...queryTemporary, ...query } }
    updateTemporaryPrescription(mergedQuery)
    const res = await inquiryPrescriptions({ ...queryTemporary, ...query })
    reloadBookingDetail()
    return res
  }

  const updateTemporaryPrescription = async (query: any) => {
    return createUpdateHealthRecord({
      ...healthRecordRef.current,
      temporary_prescription: query
    })
  }

  const updateTemporaryReExaminationDate = async (reExamDate: any) => {
    const temporaryPrescription =
      bookingDetail?.health_record?.clinic_metadata?.temporary_prescription
    if (bookingDetail?.exam_status === EExamStatus.Finished) return
    else
      await handleCreateUpdateHealthRecord({
        health_record_id: bookingDetail.health_record_id,
        temporary_prescription: {
          ...temporaryPrescription,
          query: {
            re_examination_date: reExamDate
          }
        }
      })
  }

  return (
    <ExamContext.Provider
      value={{
        ...value,
        bookingDetail,
        form,
        loading,
        templates,
        handleOnChangeForm,
        handleCreateUpdateHealthRecord,
        submitExamFinished,
        reloadBookingDetail,
        updateMedicalHistory,
        listPatientRef,
        patient,
        setTemplates,
        loadingSubmit,
        setLoadingSubmit,
        radioObstetricsGynecology,
        setRadioObstetricsGynecology: handleOnChangRadio,
        setBookings,
        bookings: bookingsRef.current,
        setReExamDate: handleSetReExamDate,
        reExamDate,
        countReExam,
        setCountReExam: handleSetCountReExam,
        updatePrescription,
        isAutoFill,
        setIsAutoFill: handleSetAutoFill
      }}>
      {children}
    </ExamContext.Provider>
  )
}
